import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import {
  GET_ALL_USERS, GET_EMPLOYEE_ENUMS, LIST_EMPLOYEES, LIST_PROSPECTS, ME_EMPLOYEE,
  SUPPORT_ESCALATION_QUEUE,
} from '../queries/staff';
import {
  CLAIM_ESCALATION, CREATE_EMPLOYEE, DEACTIVATE_EMPLOYEE, PAUSE_EMPLOYEE, REACTIVATE_EMPLOYEE,
  RELEASE_ESCALATION, RESEND_EMPLOYEE_VERIFICATION, UPDATE_EMPLOYEE,
} from '../mutations/staff';
import { SUPPORT_ESCALATION_STREAM } from '../subscriptions/support';
import {
  CONTROL_ACTIONS, askAssistant, connectSupportRoom, disconnectSupportRoom, onSupportRoom,
  sendControl, sendToAssistant, sendToCustomer, setSupportMic,
} from '../supportRoom';

/* ── the people on the platform ─────────────────────────────────────────── */

/**
 * Every account, not one advisor's book.
 *
 * This is the read the staff seat exists for: the backend answers it with all
 * users precisely because the caller is an employee. `getAllUsers` is also the
 * query that documents a known trap elsewhere in this app — the goal id on
 * these rows is always null — so nothing here should be used as a goal source.
 */
export function usePlatformUsers() {
  const { data, loading, error, refetch } = useQuery(GET_ALL_USERS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => ({
    rows: data?.getAllUsers?.items ?? [],
    total: data?.getAllUsers?.total ?? 0,
    loading,
    error,
    refetch,
  }), [data, loading, error, refetch]);
}

/** Who this staff member is in the employee register. */
export function useMeEmployee() {
  const { data, loading, error } = useQuery(ME_EMPLOYEE, { errorPolicy: 'all' });
  return { me: data?.meEmployee ?? null, loading, error };
}

export function useEmployees({ isActive = null, limit = 100 } = {}) {
  const { data, loading, error, refetch } = useQuery(LIST_EMPLOYEES, {
    variables: { isActive, limit },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return { rows: data?.listEmployees ?? [], loading, error, refetch };
}

/* Named apart from the advisor's own `useProspects`: that one is a firm's
   pipeline, this one is the platform's. */
export function usePlatformProspects(filter = null) {
  const { data, loading, error, refetch } = useQuery(LIST_PROSPECTS, {
    variables: { filter },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return {
    rows: data?.listProspects?.items ?? [],
    total: data?.listProspects?.total ?? 0,
    loading,
    error,
    refetch,
  };
}

/**
 * The role vocabularies, from the server.
 *
 * Read rather than hard-coded: app_admin ships fallback arrays for these and
 * they have already drifted from the schema, which is how a form comes to
 * offer a role the backend rejects.
 */
export function useEmployeeEnums() {
  const { data, loading, error } = useQuery(GET_EMPLOYEE_ENUMS, { errorPolicy: 'all' });
  return useMemo(() => ({
    orgRoles: data?.getEmployeeOrgRoleEnum ?? [],
    functionalRoles: data?.getEmployeeFunctionalRoleEnum ?? [],
    types: data?.getEmployeeTypeEnum ?? [],
    loading,
    error,
  }), [data, loading, error]);
}

/* Every write refreshes the register, so the list never lies about who is on. */
const REFRESH = { refetchQueries: ['ListEmployees'] };

export const useCreateEmployee = () => useMutation(CREATE_EMPLOYEE, REFRESH);
export const useUpdateEmployee = () => useMutation(UPDATE_EMPLOYEE, REFRESH);
export const useDeactivateEmployee = () => useMutation(DEACTIVATE_EMPLOYEE, REFRESH);
export const useReactivateEmployee = () => useMutation(REACTIVATE_EMPLOYEE, REFRESH);
export const usePauseEmployee = () => useMutation(PAUSE_EMPLOYEE, REFRESH);
export const useResendEmployeeVerification = () => useMutation(RESEND_EMPLOYEE_VERIFICATION);

/* ── the support desk ───────────────────────────────────────────────────── */

export const ESCALATION_TONE = {
  WAITING: 'amber',
  CLAIMED: 'blue',
  ACTIVE: 'green',
  RESOLVED: 'grey',
};

export const PRIORITY_TONE = { HIGH: 'red', URGENT: 'red', MEDIUM: 'amber', LOW: 'grey' };

/**
 * The queue of conversations waiting for a human.
 *
 * Polled AND streamed. The stream is what makes it live, but a subscription
 * that drops takes the queue with it silently, and a customer waiting unseen
 * is the one failure this screen cannot have — so a slow poll sits underneath
 * as a floor. Each event refetches rather than patching: the events carry a
 * ticket id, not a ticket, and correctness beats a round trip here.
 *
 * Deliberately does NOT hold the call. Answering opens its own screen, so the
 * call's state belongs there and to the room module — a queue that also owned
 * the live call could not survive navigating to it.
 */
export function useSupportQueue({ pollMs = 20000 } = {}) {
  const queue = useQuery(SUPPORT_ESCALATION_QUEUE, {
    variables: { limit: 50 },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    pollInterval: pollMs,
  });
  const [events, setEvents] = useState([]);
  const { refetch } = queue;

  useSubscription(SUPPORT_ESCALATION_STREAM, {
    onData: ({ data }) => {
      const ev = data?.data?.supportEscalationStream;
      if (!ev) return;
      setEvents((list) => [{ ...ev, at: new Date().toISOString() }, ...list].slice(0, 20));
      refetch().catch(() => {});
    },
    onError: () => { /* the poll above is the floor */ },
  });

  return {
    rows: queue.data?.supportEscalationQueue ?? [],
    loading: queue.loading,
    error: queue.error,
    refetch,
    events,
  };
}

/**
 * One conversation, answered.
 *
 * Owns the whole call: claiming it, the room it opens, what is said on it, and
 * the three different ways it can end. The room itself lives in
 * `supportRoom.js` as a module, so it survives this hook re-rendering and is
 * torn down only when the screen is left.
 *
 * Claiming and connecting are ONE action, because a claim with no audio is a
 * customer listening to silence.
 */
export function useSupportCall(ticketId) {
  const [claim, claimState] = useMutation(CLAIM_ESCALATION);
  const [release] = useMutation(RELEASE_ESCALATION);

  const [ticket, setTicket] = useState(null);
  const [room, setRoom] = useState({
    status: 'idle', micOn: false, participants: 0, error: null, messages: [], suggestions: [],
  });
  const [failure, setFailure] = useState(null);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => onSupportRoom(setRoom), []);

  const answer = useCallback(async () => {
    setFailure(null);
    try {
      const { data } = await claim({ variables: { ticketId: Number(ticketId) } });
      const r = data?.claimEscalation;
      if (!r?.success) {
        setFailure(r?.reason || 'Somebody else has this conversation.');
        return null;
      }
      setClaimed(true);
      setTicket(r.ticket ?? null);
      if (r.livekitToken && r.livekitWsUrl) {
        await connectSupportRoom({ token: r.livekitToken, wsUrl: r.livekitWsUrl })
          .catch((err) => setFailure(`Claimed, but the line did not open: ${err.message}`));
      } else {
        setFailure('Claimed, but the platform returned no voice room for it.');
      }
      return r;
    } catch (err) {
      setFailure(err.message);
      return null;
    }
  }, [claim, ticketId]);

  const leave = useCallback(async (action) => {
    if (action) await sendControl(action).catch(() => {});
    await disconnectSupportRoom();
    /* Releasing hands it back to the queue; closing finishes it, and the
       control message above is what tells the worker which happened. */
    if (action !== CONTROL_ACTIONS.CLOSE && ticketId != null) {
      await release({ variables: { ticketId: Number(ticketId) } }).catch(() => {});
    }
    setClaimed(false);
  }, [release, ticketId]);

  /* Leaving the screen must not leave a customer on a held line. */
  useEffect(() => () => { disconnectSupportRoom(); }, []);

  return {
    ticket,
    room,
    failure,
    claimed,
    claiming: claimState.loading,
    answer,
    handBack: () => leave(CONTROL_ACTIONS.HANDOVER_TO_AI),
    release: () => leave(null),
    close: () => leave(CONTROL_ACTIONS.CLOSE),
    setMic: setSupportMic,
    sendToCustomer,
    sendToAssistant,
    askAssistant,
  };
}
